/* global console, Excel, Office */
import constants from "./constants?v=2";
import Papa from 'papaparse';
import LogHelper from "./logHelper";
import QuickBooks from './quickBooks';

const logHelper = new LogHelper();
const quickBooks = new QuickBooks();

export async function getSheetByName(context, sheetName) {
  try {
    let sheet = context.workbook.worksheets.getItem(sheetName);
    sheet.load("name");
    await context.sync();
    console.log(`We found sheet with name ${sheet.name}`);
    await logHelper.writeLog(logHelper.LogTypes.Msg, `We found sheet with name '${sheet.name}'.`);
    return sheet;
  } catch (error) {
    console.log(`Error while getting sheet with name ${sheetName}. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while getting sheet with name '${sheetName}'. Error details are, ${error}`);
    return null;
  }
}

export async function clearSheet(context, sheet) {
  try {
    let usedRange = sheet.getUsedRange();
    usedRange.load("address");
    await context.sync();
    console.log(`Clearing used range: ${usedRange.address}`);
    usedRange.clear();
    await context.sync();
    console.log("Sheet cleared successfully!");
    await logHelper.writeLog(logHelper.LogTypes.Msg, "Sheet cleaned successfully.");
  } catch (error) {
    console.log(`Error while clearing ${constants.ImportDataSheetName} sheet. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while clearing '${constants.ImportDataSheetName}' sheet. Error details are, ${error}`);
  }
}

export async function displayData(fileName, data) {
  try {
    // Validate data
    if (!data || !Array.isArray(data) || data.length === 0 || !data.every(row => Array.isArray(row))) {
      throw new Error("Invalid data: Must be a non-empty 2D array");
    }

    // Normalize data to ensure consistent row lengths and string values
    const maxColumns = Math.max(...data.map(row => row.length));
    const normalizedData = data.map(row => {
      const newRow = Array(maxColumns).fill('');
      for (let i = 0; i < Math.min(row.length, maxColumns); i++) {
        newRow[i] = row[i] != null ? String(row[i]) : '';
      }
      return newRow;
    });

    await Excel.run(async (context) => {
      // Get or create the import sheet
      let saleImportSheet = await getSheetByName(context, constants.ImportDataSheetName);
      if (!saleImportSheet) {
        console.log(`Sheet '${constants.ImportDataSheetName}' not found, creating new sheet`);
        saleImportSheet = context.workbook.worksheets.add(constants.ImportDataSheetName);
        saleImportSheet.load("name");
        await context.sync();
        await logHelper.writeLog(logHelper.LogTypes.Msg, `Created sheet '${constants.ImportDataSheetName}'`);
      }

      // Clear the sheet
      console.log(`Clearing sheet '${constants.ImportDataSheetName}'`);
      await clearSheet(context, saleImportSheet);
      await context.sync();

      // Calculate range address
      if (maxColumns === 0 || normalizedData.length === 0) {
        throw new Error("No data to write: Empty columns or rows");
      }

      // Handle large column counts safely
      const endColumnLetter = getColumnLetter(maxColumns);
      const rangeAddress = `A1:${endColumnLetter}${normalizedData.length}`;
      console.log(`Writing data to range: ${rangeAddress}, rows: ${normalizedData.length}, columns: ${maxColumns}`);

      // Write data
      const range = saleImportSheet.getRange(rangeAddress);
      range.values = normalizedData;
      await context.sync();
      console.log(`Data written to range ${rangeAddress}`);

      // Format the range
      range.format.autofitColumns();
      range.format.autofitRows();
      await context.sync();
      console.log(`Formatted range ${rangeAddress}`);

      await logHelper.writeLog(logHelper.LogTypes.Msg, `CSV successfully copied to '${constants.ImportDataSheetName}' sheet.`);

      // Export to CSV
      let isImportedDataValid = await exportDataAsCsv(fileName);
      await pushDataToQBO(fileName, isImportedDataValid);
    });
  } catch (error) {
    console.log("Error adding csv data to workbook sheets: ", error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error adding csv data to workbook sheets. Error details are, ${error}`);
    throw error;
  }
}

// Helper function to calculate column letter for large column counts
function getColumnLetter(columnCount) {
  if (columnCount <= 0) return 'A';
  let letter = '';
  let num = columnCount - 1;
  while (num >= 0) {
    letter = String.fromCharCode(65 + (num % 26)) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
}

export async function isImportedDataValid(context, fileName) {
  try {
    const dateCount = context.workbook.names.getItem(constants.WorkBookNames.DateCount);
    const dateCountRange = dateCount.getRange();
    dateCountRange.load("values");
    const jeBalance = context.workbook.names.getItem(constants.WorkBookNames.JournalEntryBalance);
    const jeBalanceRange = jeBalance.getRange();
    jeBalanceRange.load("values");
    const knownStore = context.workbook.names.getItem(constants.WorkBookNames.KnownStore);
    const knownStoreRange = knownStore.getRange();
    knownStoreRange.load("values");
    await context.sync();
    let isDateValid = !dateCountRange.isNullObject && dateCountRange.values.length > 0 && dateCountRange.values[0][0] === "Pass";
    let isBalanceValid = !jeBalanceRange.isNullObject && jeBalanceRange.values.length > 0 && jeBalanceRange.values[0][0] === "Pass";
    let isStoreValid = !knownStoreRange.isNullObject && knownStoreRange.values.length > 0 && knownStoreRange.values[0][0] === "Pass";
    bindValidationResult(fileName, isStoreValid, isDateValid, isBalanceValid);
    return isStoreValid && isDateValid && isBalanceValid;
  } catch (error) {
    console.log(`Error while validating imported csv data for '${fileName}'. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while validating imported csv data for '${fileName}'. Error details are, ${error}`);
    return false;
  }
}

export async function getNamedValueAsText(context, name) {
  try {
    const nameItem = context.workbook.names.getItem(name);
    const nameItemRange = nameItem.getRange();
    nameItemRange.load("values");
    await context.sync();
    if (!nameItemRange.isNullObject && nameItemRange.values.length > 0 && nameItemRange.values[0][0]) {
      return nameItemRange.values[0][0];
    }
  } catch (error) {
    console.log(`Error while getting named value of '${name}'. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while getting named value of '${name}'. Error details are, ${error}`);
  }
  return name;
}

export async function exportDataAsCsv(fileName) {
  let isImportedDataAreValid = false;
  await Excel.run(async (context) => {
    try {
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Data refresh triggered for '${fileName}'.`);
      context.workbook.application.calculate(Excel.CalculationMode.full);
      await context.sync();
      await logHelper.writeLog(logHelper.LogTypes.Msg, "Data refreshed.");

      const names = context.workbook.names;
      names.load("items");
      await context.sync();

      if (!await isImportedDataValid(context, fileName)) {
        await logHelper.writeLog(logHelper.LogTypes.Error, `Invalid data. Skipping export for '${fileName}'.`);
        return;
      }
      else {
        isImportedDataAreValid = true; // Set flag to true if data is valid
      }
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Data is valid. Exporting '${fileName}'.`);
      let sheet = await getSheetByName(context, constants.ExportDataSheetName);
      let usedRange = sheet.getUsedRange();
      usedRange.load("values");
      await context.sync();

      let classValue = await getNamedValueAsText(context, constants.WorkBookNames.Class);
      let startDateValue = await getNamedValueAsText(context, constants.WorkBookNames.StartDate);
      if (startDateValue && typeof startDateValue === "number") {
        startDateValue = new Date(Date.UTC(1899, 11, 30) + startDateValue * 24 * 60 * 60 * 1000);
        startDateValue = getDateInStringFormat(startDateValue);
      }

      let csvContent = Papa.unparse(usedRange.values);
      const finalFileName = `${classValue}_JE_${startDateValue}.csv`;
      const filesystemOption = Office.context.document.settings.get('filesystemOption') || 'Downloads';

      let responses;
      if (filesystemOption === 'Downloads' && Office.context.platform !== Office.PlatformType.Mac) {
        await saveFileViaBlob(csvContent, finalFileName);
        // maybe add feedback to user notifying that downloads option can't work on Mac
        responses = [];
      } else if (filesystemOption === 'Excel Folder') {
        responses = await saveFileViaVBA(constants.ExportDataSheetName, finalFileName);
      }

      updateFileCompleteIcon(fileName, filesystemOption, responses[0]?.response, responses[0]?.status);

      await logHelper.writeLog(logHelper.LogTypes.Msg, `CSV exported successfully for '${fileName}'.`);
    } catch (error) {
      const errorDetails = `${error.message} | Stack: ${error.stack || "N/A"}`;
      console.error(`Export error for '${fileName}': `, error);
      await logHelper.writeLog(logHelper.LogTypes.Error, `Export error for '${fileName}': ${errorDetails}`);
    }
  });
  return isImportedDataAreValid;
}

async function saveFileViaBlob(csvContent, finalFileName) {
  await logHelper.writeLog(logHelper.LogTypes.Msg, `Initiating download of CSV file: ${finalFileName}`);
  let blob = new Blob([csvContent], { type: "text/csv" });
  let url = URL.createObjectURL(blob);
  let a = document.createElement("a");
  a.href = url;
  a.download = finalFileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function saveFileViaVBA(sheetName, finalFileName) {
  await logHelper.writeLog(logHelper.LogTypes.Msg, "Using VBA to save CSV to Excel Folder");
  let requestsData = [{ request: "ExportWorksheetToCSV", parameters: `Exported JEs||${finalFileName}||${sheetName}` }];
  const responses = await submitAndAwaitRequests(requestsData);
  return responses;
}

function updateFileCompleteIcon(fileElement, saveMethod, path, status) {
  const fileDiv = document.getElementById(fileElement);
  if (fileDiv) {
    const oldButton = fileDiv.parentElement.getElementsByClassName("remove-btn")[0];
    if (oldButton) {
      const newButton = document.createElement("button");
      newButton.className = "completed-btn";

      switch (status) {
        case "Error":
          newButton.innerHTML = `<i class="ms-Icon ms-Icon--Warning" aria-hidden="true"></i>`;
          break;

        case "Complete":
          if (saveMethod === 'Excel Folder' && path) {
            newButton.innerHTML = `<i class="ms-Icon ms-Icon--FolderHorizontal" aria-hidden="true"></i>`;
            newButton.addEventListener('click', () => {
              let requestsData = [{ request: "OpenFolder", parameters: path }];
              submitAndAwaitRequests(requestsData);
            });
          } else {
            newButton.innerHTML = `<i class="ms-Icon ms-Icon--Completed" aria-hidden="true"></i>`;
          }
          break;

        default:
          newButton.innerHTML = `<i class="ms-Icon ms-Icon--StatusCircleQuestionMark" aria-hidden="true"></i>`;
          break;
      }

      oldButton.parentNode.replaceChild(newButton, oldButton);
    }
  }
}

function bindValidationResult(fileName, isStoreValid, isDateValid, isBalanceValid) {
  let fileDiv = document.getElementById(fileName);
  if (fileDiv) {
    let validationHTML = `<span>Store: <span class="${isStoreValid ? "pass" : "failed"}">${isStoreValid ? "✔" : "✘"}</span></span>|
                          <span>Dates: <span class="${isDateValid ? "pass" : "failed"}">${isDateValid ? "✔" : "✘"}</span></span>|
                          <span>Bal.: <span class="${isBalanceValid ? "pass" : "failed"}">${isBalanceValid ? "✔" : "✘"}</span></span>`;
    const validationSection = fileDiv.getElementsByClassName("validationSection")[0];
    if (validationSection) {
      validationSection.classList.remove("hide");
      validationSection.innerHTML = validationHTML;
    }
  }
}

function getDateInStringFormat(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function excelDateToJSDate(serial) {
  // Convert Excel serial date to JavaScript Date object
  // Excel's base date is different from JavaScript's.
  // Excel's base date is January 1, 1900, while JavaScript's is January 1, 1970.
  // The constant 25569 represents the number of days between Jan 1, 1900 and Jan 1, 1970.
  // 86400 * 1000 converts days to milliseconds.
  const javaScriptDate = new Date(Math.round((serial - 25569) * 86400 * 1000));
  return javaScriptDate
}

function isDateFormat(format) {
  const dateKeywords = ["yy", "dd", "mm", "hh", "ss"];
  return dateKeywords.some(keyword => format.toLowerCase().includes(keyword));
}

export async function getSheetData() {
  let csvData = null;
  try {
    // let sheetName = "Sheet1";
    let sheetName = constants.ExportDataSheetName;
    await Excel.run(async (context) => {
      let sheet = await getSheetByName(context, sheetName);
      let usedRange = sheet.getUsedRange();
      usedRange.load(["values", "numberFormat"]);
      await context.sync();
      const values = usedRange.values;
      const formats = usedRange.numberFormat;

      const formattedData = [];

      for (let i = 0; i < values.length; i++) {
        const row = [];
        for (let j = 0; j < values[i].length; j++) {
          const value = values[i][j];
          const format = formats[i][j];

          // Check if it's a date format
          if (typeof value === "number" && isDateFormat(format)) {
            row.push(excelDateToJSDate(value).toISOString().split('T')[0]);
          } else {
            row.push(value);
          }
        }
        formattedData.push(row);
      }
      const dataInCsvFormat = Papa.unparse(formattedData)
      csvData = Papa.parse(dataInCsvFormat, {
        header: true,
        skipEmptyLines: true
      });
    });
  } catch (error) {
    console.log("Error reading csv data to workbook sheets, ", error);
    //await logHelper.writeLog(logHelper.LogTypes.Error, `Error adding csv data to workbook sheets. Error details are, ${error}`);
  }
  return csvData;
}

// This function is start the process to push data to QBO and get the response
// it will be called after the data is imported and validated
export async function pushDataToQBO(filename, isImportedDataValid) {
  //console.log("Excel Utils initialized.");
  const qboUploadCheckbox = document.getElementById('qboUploadCheckbox');
  if (qboUploadCheckbox.checked) {
    if (!isImportedDataValid) {
      await logHelper.writeLog(logHelper.LogTypes.Error, `Invalid data: ${filename}. Skipped to push data on QBO.`);
      return;
    }
    await logHelper.writeLog(logHelper.LogTypes.Msg, "Initialized to push data QBO.");
    const sheetData = await getSheetData();
    console.log("Sheet data", sheetData);
    if (sheetData && sheetData.data.length > 0) {
      const result = await quickBooks.pushJournalEntryOnQbo(sheetData.data);
      if (result && result.ok === true) {
        let isAnyError = false;
        if (result.data && result.data.BatchItemResponse.length > 0) {
          {
            for (let i = 0; i < result.data.BatchItemResponse.length; i++) {
              const entry = result.data.BatchItemResponse[i];
              let journalEntryID = (entry.bId).split("#")[1];
              if (entry.Fault) {
                isAnyError = true;
                for (let j = 0; j < entry.Fault.Error.length; j++) {
                  const errorInfo = entry.Fault.Error[j];
                  await logHelper.writeLog(logHelper.LogTypes.Error, `Error in pushing data for journal number:${journalEntryID}. Error Type:${entry.Fault.type} | Error Detail:${errorInfo.Detail} | Error Message:${errorInfo.Message} | Error Code:${errorInfo.code}`);
                }

                //await logHelper.writeLog(logHelper.LogTypes.Error, `Error in pushing data for entry ${i + 1}: ${entry.message}`);
              } else {
                await logHelper.writeLog(logHelper.LogTypes.Msg, `Data pushed successfully for journal number: ${journalEntryID}`);
              }
            }
          }
          if (isAnyError) {
            validationQBOPostData(true, filename);
            await logHelper.writeLog(logHelper.LogTypes.Error, `Some errors occurred while pushing data to QBO. Please check the following logs for details.`);
          } else {
            validationQBOPostData(false, filename);
            await logHelper.writeLog(logHelper.LogTypes.Msg, `Data pushed successfully to QBO for file: ${filename}`);
          }
        } else {
          await logHelper.writeLog(logHelper.LogTypes.Error, `Failed to push data to QBO for file: ${filename}`);
        }
      }
    }
    else {
      await logHelper.writeLog(logHelper.LogTypes.Warn, "No data found in the sheet to push on QBO.");
    }
  }
  else {
    await logHelper.writeLog(logHelper.LogTypes.Msg, "Skipped to push data on QBO.");
  }
}

// this method is used to display failed or passed status of journal entry data on QBO
// it will be called after the journal entry data is pushed to QBO
export async function validationQBOPostData(isJournalEntryFailed, fileName) {
  let fileDiv = document.getElementById(fileName);
  if (fileDiv) {
    let validationHTML = ` | <span>QBO.: <span class="${!isJournalEntryFailed ? "pass" : "failed"}">${!isJournalEntryFailed ? "✔" : "✘"}</span></span>`;
    const validationSection = fileDiv.getElementsByClassName("validationSection")[0];
    if (validationSection) {
      validationSection.innerHTML += validationHTML;
    }
  }
}